/**
 * AccountDetail — everything we hold on one Titan Treasure account, over its
 * full history, with the redemption verdict.
 * Data: GET /analytics/tt/user (profile, cards, links, devices) and /user/transactions.
 */
import React, { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Activity, ArrowLeft, CircleCheck, CircleX, Clock, CreditCard, ShieldCheck, TriangleAlert } from "lucide-react";
import { Alert, Button } from "../../../design/primitives";
import { useTTQuery } from "../ttApi";
import {
  OUTCOME_LABELS,
  THREE_DS_LABELS,
  bucketLabel,
  card,
  count,
  dateOnly,
  dateTime,
  money,
  moneyExact,
  partyLabel,
  shortId,
} from "../ttFormat";
import {
  AccountLinks,
  ChartBox,
  Fact,
  INK,
  LoadState,
  Note,
  OUTCOME_COLORS,
  PartyPill,
  Pager,
  Pill,
  RankBars,
  ReviewCard,
  ReviewGrid,
  TONE,
  Table,
  Tile,
  Tiles,
  VERDICT_TONES,
  VerdictPill,
  Wrap,
  axisProps,
} from "../ttKit";

const TX_PAGE_SIZE = 20;

// Checks on the player before any payout, for HOLD and REVIEW.
const VERIFY_PLAYER = [
  "Verify the player's identity: the name on their ID should match the account name and the cardholder name on " +
    "every card they deposited with (see Identity signals and Cards).",
  "Ask for proof they own those cards, such as a photo of the card or a bank statement showing their name and " +
    "the last 4 digits.",
];

// What Titan Treasure's team does with a withdrawal request, per verdict.
const GUIDANCE = {
  HOLD: {
    tone: "danger",
    title: "Do not pay. Escalate.",
    when: "The account is blacklisted, or shares a card or device with a blacklisted account.",
    steps: [
      ...VERIFY_PLAYER,
      "Check whether the player is the same person as the blacklisted accounts they share a card or device with " +
        "(see Linked accounts).",
    ],
  },
  REVIEW: {
    tone: "warning",
    title: "Check before paying.",
    when:
      "A bank fraud refusal, a card reported lost or stolen, a deposit on a card later refused for fraud, " +
      "a card or device shared with another account, or the account is new.",
    steps: VERIFY_PLAYER,
  },
  OK: {
    tone: "success",
    title: "Pay.",
    when: "Whitelisted with no hold reason, or none of the risk signals.",
    steps: [],
  },
};

const OUTCOME_TONES = { completed: "green", fraud_decline: "red", declined: "amber", blocked: "blue", abandoned: "slate" };

export default function AccountDetail({ account, fresh, onOpenAccount, onBack }) {
  const detail = useTTQuery("user", { account, fresh });
  const email = detail.data?.profile?.email;
  const [txPage, setTxPage] = useState(1);
  useEffect(() => setTxPage(1), [account]);
  const txns = useTTQuery("user/transactions", { email, page: txPage, limit: TX_PAGE_SIZE, fresh }, { enabled: !!email });

  return (
    <>
      <Button size="sm" variant="ghost" iconLeft={<ArrowLeft size={14} />} onClick={onBack} style={{ marginBottom: 14 }}>
        All accounts
      </Button>

      <LoadState state={detail} height={240}>
        {(d) => (
          <>
            <Overview data={d} />

            <ReviewCard eyebrow="Cards" title="Cards" desc="Every card this account used, and who else used it.">
              <Table
                columns={CARD_COLUMNS(onOpenAccount)}
                rows={d.cards.map((r) => ({ key: r.card_key, ...r }))}
                emptyState="No cards."
              />
            </ReviewCard>

            <ReviewGrid min={480}>
              <ReviewCard eyebrow="Links" title="Linked accounts" desc="Share a card or device with this account.">
                <Table
                  columns={LINKED_COLUMNS}
                  rows={d.linked_accounts.map((r) => ({ key: r.email, ...r, onClick: () => onOpenAccount(r.email) }))}
                  emptyState="No other account shares a card or device with this one."
                />
              </ReviewCard>
              <ReviewCard eyebrow="Links" title="Devices" desc="Device fingerprints seen on this account's attempts.">
                <Table
                  columns={DEVICE_COLUMNS(onOpenAccount)}
                  rows={d.devices.map((r) => ({ key: r.visitor_id, ...r }))}
                  emptyState="No device fingerprint on this account's attempts."
                />
              </ReviewCard>
            </ReviewGrid>
          </>
        )}
      </LoadState>

      {email && (
        <>
          <ReviewCard eyebrow="Timeline" title="Transaction timeline" desc="Every attempt, newest first.">
            <LoadState state={txns}>
              {(t) => (
                <>
                  <Table columns={TX_COLUMNS} rows={t.rows.map((r) => ({ key: r.request_id, ...r }))} emptyState="No attempts." />
                  <Pager page={txPage} limit={TX_PAGE_SIZE} total={t.total} onPage={setTxPage} />
                </>
              )}
            </LoadState>
          </ReviewCard>

          {detail.data && (
            <ReviewCard eyebrow="Lists" title="List history" desc="Blacklist, whitelist and 3DS changes logged for this account.">
              <Table
                columns={HISTORY_COLUMNS}
                rows={detail.data.list_history.map((r, i) => ({ key: `${r.changed_at}-${i}`, ...r }))}
                emptyState="No logged list changes. The current status is shown at the top."
              />
            </ReviewCard>
          )}
        </>
      )}
    </>
  );
}

function Overview({ data }) {
  const p = data.profile;
  const g = GUIDANCE[p.verdict] || GUIDANCE.OK;
  const f = data.three_ds_funnel || {};
  const daily = (data.daily || []).map((x) => ({ label: bucketLabel(x.day, "day"), ...x }));

  return (
    <>
      <ReviewCard
        eyebrow="Account"
        title={p.email}
        desc={p.raw_email && p.raw_email !== p.email ? `Typed as ${p.raw_email}` : null}
        tag={p.verdict}
        tagTone={VERDICT_TONES[p.verdict] || "slate"}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
          <div style={{ minWidth: 280, flex: "1 1 360px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16 }}>
              <Fact label="Titan Treasure user ID">{p.tt_user_id || "—"}</Fact>
              <Fact label="Name on account">{p.account_name || "—"}</Fact>
              <Fact label="List status">
                <PartyPill status={p.party_status} />
              </Fact>
              <Fact label="Status since">
                {p.status_since ? `${dateOnly(p.status_since)}${p.status_by ? ` by ${p.status_by}` : ""}` : "—"}
              </Fact>
              <Fact label="First seen">{dateTime(p.first_seen)}</Fact>
              <Fact label="Last seen">{dateTime(p.last_seen)}</Fact>
              <Fact label="Days active">{count(p.days_active)}</Fact>
            </div>
          </div>

          <div style={{ flex: "1 1 360px", maxWidth: 560 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 12, color: INK.muted }}>Redemption verdict</span>
              <VerdictPill verdict={p.verdict} />
            </div>
            <Alert
              tone={g.tone}
              title={g.title}
              description={
                <>
                  {p.reasons?.length > 0 && <div style={{ marginBottom: 8 }}>Because: {p.reasons.join("; ")}.</div>}
                  {g.steps.length > 0 && (
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {g.steps.map((s) => (
                        <li key={s}>{s}</li>
                      ))}
                    </ul>
                  )}
                </>
              }
            />
            <Note style={{ marginTop: 8 }}>
              {g.when}
            </Note>
          </div>
        </div>
      </ReviewCard>

      <Tiles>
        <Tile tone="blue" icon={Activity} label="Attempts" value={count(p.attempts)} />
        <Tile tone="green" icon={CircleCheck} label="Completed" value={money(p.completed_usd)} sub={`${count(p.completed_n)} deposits`} />
        <Tile tone="amber" icon={CircleX} label="Declined by bank" value={money(p.declined_usd)} sub={`${count(p.declined_n)} attempts`} />
        <Tile tone="green" icon={ShieldCheck} label="Blocked by Idenza" value={money(p.blocked_usd)} sub={`${count(p.blocked_n)} attempts`} />
        <Tile
          tone="red"
          icon={TriangleAlert}
          label="Fraud declines"
          value={count(p.fraud_declines)}
          sub={`${money(p.fraud_decline_usd)} · ${count(p.stolen_reports)} lost/stolen`}
        />
      </Tiles>

      <Tiles>
        <Tile
          tone="amber"
          icon={CreditCard}
          label="Completed on cards later refused for fraud"
          value={money(p.completed_usd_card_later_fd)}
          sub="Likely to come back as chargebacks"
        />
        <Tile tone="green" icon={ShieldCheck} label="Completed with 3DS passed" value={money(p.completed_usd_3ds_auth)} sub="Bank usually carries the fraud loss" />
        <Tile
          tone="amber"
          icon={Clock}
          label="Completed in the last 9 days"
          value={money(p.completed_usd_recent)}
          sub="Too recent to judge: fraud reports arrive ~5 days after the charge"
        />
      </Tiles>

      <ReviewCard eyebrow="Identity" title="Identity signals" desc="Compare these with the ID and card proof the player sends.">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 20 }}>
          <Fact label="Cards">{count(p.cards)} ({count(p.cards_shared)} shared, {count(p.cards_with_fraud_decline)} fraud-declined)</Fact>
          <Fact label="Card ranges">{count(p.bins)}</Fact>
          <Fact label="Cardholder names">
            <span title={p.cardholder_name_list || ""}>
              {count(p.cardholder_names)}
              {p.cardholder_name_list ? `: ${p.cardholder_name_list}` : ""}
            </span>
          </Fact>
          <Fact label="Devices">{count(p.devices)} ({count(p.devices_shared)} shared)</Fact>
          <Fact label="IP addresses">{count(p.ips)}</Fact>
          <Fact label="Countries">{count(p.ip_countries)}</Fact>
          <Fact label="VPN attempts">{count(p.vpn_txns)}</Fact>
          <Fact label="Highest risk score">{p.max_v3_score == null ? "—" : Number(p.max_v3_score).toFixed(2)}</Fact>
          <Fact label="Linked to blacklisted (card)">{p.linked_blacklisted_by_card || "—"}</Fact>
          <Fact label="Linked to blacklisted (device)">{p.linked_blacklisted_by_device || "—"}</Fact>
          <Fact label="Top rules">{p.top_rules || "—"}</Fact>
        </div>
      </ReviewCard>

      <ReviewGrid min={360}>
        <ReviewCard eyebrow="3DS" title="3DS steps" desc="Deposits reaching each step.">
          <RankBars
            label="step"
            value="n"
            barColor={TONE.green.fg}
            empty="No 3DS activity."
            rows={[
              { step: "1. 3DS required", n: f.required || 0 },
              { step: "2. 3DS started", n: f.started || 0 },
              { step: "3. Cardholder authenticated", n: f.authenticated || 0 },
              { step: "4. Deposit completed", n: f.completed || 0 },
              { step: "5. Bank took the fraud liability", n: f.liability_shift || 0 },
            ]}
          />
        </ReviewCard>
        <ReviewCard eyebrow="Declines" title="Top decline reasons" desc="Bank decline codes.">
          <RankBars rows={data.decline_reasons} label="reason" value="n" barColor={TONE.amber.fg} empty="No bank declines." />
        </ReviewCard>
      </ReviewGrid>

      <ReviewCard eyebrow="Activity" title="Daily activity" desc="Attempts per day by outcome.">
        <ChartBox height={240}>
          <ResponsiveContainer>
            <BarChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={INK.line} />
              <XAxis dataKey="label" {...axisProps} minTickGap={16} />
              <YAxis {...axisProps} allowDecimals={false} width={32} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="completed" name="Completed" stackId="a" fill={OUTCOME_COLORS.completed} />
              <Bar dataKey="declined" name="Declined" stackId="a" fill={OUTCOME_COLORS.declined} />
              <Bar dataKey="blocked" name="Blocked" stackId="a" fill={OUTCOME_COLORS.blocked} />
              <Bar dataKey="abandoned" name="Abandoned" stackId="a" fill={OUTCOME_COLORS.abandoned} />
            </BarChart>
          </ResponsiveContainer>
        </ChartBox>
      </ReviewCard>
    </>
  );
}

const CARD_COLUMNS = (onOpenAccount) => [
  { key: "card", label: "Card", render: (r) => card(r.bin, r.last4, r.card_key) },
  { key: "names", label: "Names typed", render: (r) => <Wrap max={240}>{(r.names_typed || []).join(", ") || "—"}</Wrap> },
  { key: "attempts", label: "Attempts", numeric: true, render: (r) => count(r.attempts) },
  { key: "completed_usd", label: "Completed", numeric: true, render: (r) => money(r.completed_usd) },
  {
    key: "fraud_declines",
    label: "Fraud declines",
    numeric: true,
    render: (r) =>
      r.fraud_declines_all_accounts > r.fraud_declines
        ? `${count(r.fraud_declines)} (${count(r.fraud_declines_all_accounts)} all accounts)`
        : count(r.fraud_declines),
  },
  { key: "last_fd", label: "Last fraud decline", render: (r) => dateTime(r.last_fraud_decline_at) },
  { key: "other", label: "Other accounts on this card", render: (r) => <AccountLinks emails={r.other_accounts} onOpen={onOpenAccount} max={320} /> },
];

const LINKED_COLUMNS = [
  { key: "email", label: "Account", render: (r) => <span style={{ color: TONE.blue.text }}>{r.email}</span> },
  {
    key: "via",
    label: "Linked by",
    render: (r) => (r.by_card && r.by_device ? "Card and device" : r.by_card ? "Shared card" : "Shared device"),
  },
  { key: "party_status", label: "List status", render: (r) => <PartyPill status={r.party_status} /> },
  { key: "fraud_declines", label: "Fraud declines", numeric: true, render: (r) => count(r.fraud_declines) },
  { key: "completed_usd", label: "Completed", numeric: true, render: (r) => money(r.completed_usd) },
  { key: "last_seen", label: "Last seen", render: (r) => dateOnly(r.last_seen) },
];

const DEVICE_STATUS = { blacklist: ["red", "Blacklisted"], whitelist: ["green", "Trusted"], force_3ds: ["amber", "Forced 3DS"], none: ["slate", "None"] };

const DEVICE_COLUMNS = (onOpenAccount) => [
  { key: "visitor_id", label: "Device", render: (r) => <span title={r.visitor_id}>{shortId(r.visitor_id, 12)}</span> },
  {
    key: "status",
    label: "Status",
    render: (r) => {
      const [tone, label] = DEVICE_STATUS[(r.status || "none").split(",")[0].trim()] || DEVICE_STATUS.none;
      return (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <Pill tone={tone}>{label}</Pill>
          {r.blacklisted_any_account && !String(r.status).includes("blacklist") && (
            <Pill tone="red">Blacklisted on another account</Pill>
          )}
        </div>
      );
    },
  },
  { key: "first_seen", label: "First seen", render: (r) => dateOnly(r.first_seen) },
  { key: "last_seen", label: "Last seen", render: (r) => dateOnly(r.last_seen) },
  { key: "attempts", label: "Attempts", numeric: true, render: (r) => count(r.attempts) },
  { key: "other", label: "Other accounts on this device", render: (r) => <AccountLinks emails={r.other_accounts} onOpen={onOpenAccount} max={280} /> },
];

const TX_COLUMNS = [
  { key: "scored_at", label: "Time", render: (r) => dateTime(r.scored_at) },
  { key: "amount", label: "Amount", numeric: true, render: (r) => moneyExact(r.amount) },
  { key: "card", label: "Card", render: (r) => card(r.bin, r.last4, r.card_key) },
  {
    key: "outcome",
    label: "Outcome",
    render: (r) => <Pill tone={OUTCOME_TONES[r.outcome] || "slate"}>{OUTCOME_LABELS[r.outcome] || r.outcome}</Pill>,
  },
  { key: "decline_reason", label: "Decline reason", render: (r) => r.decline_reason || "—" },
  { key: "three_ds", label: "3DS result", render: (r) => THREE_DS_LABELS[r.three_ds_result] || "—" },
  { key: "visitor_id", label: "Device", render: (r) => <span title={r.visitor_id || ""}>{shortId(r.visitor_id, 10)}</span> },
  { key: "ip", label: "IP and country", render: (r) => (r.ip ? `${r.ip}${r.ip_country ? ` · ${r.ip_country}` : ""}${r.vpn ? " · VPN" : ""}` : "—") },
  { key: "processor_transfer_id", label: "Processor transfer ID", render: (r) => r.processor_transfer_id || "—" },
];

const HISTORY_COLUMNS = [
  { key: "changed_at", label: "When", render: (r) => dateTime(r.changed_at) },
  { key: "action", label: "Change", render: (r) => partyLabel(r.action) },
  { key: "message", label: "Details", render: (r) => <Wrap max={480}>{r.message || "—"}</Wrap> },
  { key: "changed_by", label: "By", render: (r) => r.changed_by || "—" },
];
